// ActiveArticleProvider.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useArticlesData } from "./ArticlesDataProvider";
import { Article } from "../shared/types/DataTypes";
import { fetchFullArticleDetails } from "../services/apiService";

interface ActiveArticleContextValue {
  activeArticle: Article | null;
  previousArticle: Article | null;
  nextArticle: Article | null;
  setActiveArticleId: (id: string) => void;
  goNext: () => void;
  goPrevious: () => void;
}

export const ActiveArticleContext =
  createContext<ActiveArticleContextValue | null>(null);

export function ActiveArticleProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    articles,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useArticlesData();

  const [activeArticleId, setActiveArticleId] = useState<string>("");

  /**
   * O(1) index lookup
   */
  const indexMap = useMemo(() => {
    const map = new Map<string, number>();
    articles.forEach((a, i) => map.set(a.id, i));
    return map;
  }, [articles]);

  const hasActiveId = !!activeArticleId;
  const hasActiveInList = hasActiveId && indexMap.has(activeArticleId);
  const activeIndex = hasActiveId ? (indexMap.get(activeArticleId) ?? -1) : 0;

  const [resolvedActive, setResolvedActive] = useState<Article | null>(null);
  const resolvedIdRef = useRef<string | null>(null);

  const activeArticle =
    activeIndex >= 0 ? (articles[activeIndex] ?? null) : resolvedActive;
  const previousArticle = activeIndex > 0 ? (articles[activeIndex - 1] ?? null) : null;
  const nextArticle = activeIndex >= 0 ? (articles[activeIndex + 1] ?? null) : null;

  /**
   * Boundary-aware pagination (NO loops)
   */
  const fetchLock = useRef(false);

    useEffect(() => {
    if (!hasActiveId) return;

    if (hasActiveInList) {
      if (resolvedActive) setResolvedActive(null);
      resolvedIdRef.current = null;
      return;
    }

    if (resolvedIdRef.current === activeArticleId) return;
    resolvedIdRef.current = activeArticleId;

    let cancelled = false;
    fetchFullArticleDetails(activeArticleId)
      .then((full: any) => {
        if (cancelled) return;
        setResolvedActive(full as Article);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [activeArticleId, hasActiveId, hasActiveInList, resolvedActive]);

  useEffect(() => {
    if (fetchLock.current) return;
    if (hasActiveId && !hasActiveInList) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchLock.current = true;
        fetchNextPage().finally(() => {
          fetchLock.current = false;
        });
      } else if (hasPreviousPage && !isFetchingPreviousPage) {
        fetchLock.current = true;
        fetchPreviousPage().finally(() => {
          fetchLock.current = false;
        });
      }
      return;
    }

    if (!nextArticle && hasNextPage && !isFetchingNextPage) {
      fetchLock.current = true;
      fetchNextPage().finally(() => {
        fetchLock.current = false;
      });
    }

    if (!previousArticle && hasPreviousPage && !isFetchingPreviousPage) {
      fetchLock.current = true;
      fetchPreviousPage().finally(() => {
        fetchLock.current = false;
      });
    }
  }, [
    nextArticle,
    previousArticle,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    hasActiveId,
    hasActiveInList,
  ]);

  /**
   * Navigation helpers
   */
  const goNext = useCallback(() => {
    if (nextArticle) setActiveArticleId(nextArticle.id);
  }, [nextArticle]);

  const goPrevious = useCallback(() => {
    if (previousArticle) setActiveArticleId(previousArticle.id);
  }, [previousArticle]);

  const value = useMemo(
    () => ({
      activeArticle,
      previousArticle,
      nextArticle,
      setActiveArticleId,
      goNext,
      goPrevious,
    }),
    [activeArticle, previousArticle, nextArticle, goNext, goPrevious]
  );

  return (
    <ActiveArticleContext.Provider value={value}>
      {children}
    </ActiveArticleContext.Provider>
  );
}

export function useActiveArticle() {
  const ctx = useContext(ActiveArticleContext);
  if (!ctx) {
    throw new Error("useActiveArticle must be used within ActiveArticleProvider");
  }
  return ctx;
}
